import { useTranslation } from 'react-i18next';

import Avatar from '@/core/components/Avatar.tsx';
import ColorSwatchPicker, { type ColorSwatchPickerProps } from '@/core/components/ColorSwatchPicker.tsx';

import { AVATAR_COLORS, type AvatarColorName } from './avatarColors.ts';

// Radio values are strings, so the hue is converted; NO_COLOR stands for null (a grey avatar).
const NO_COLOR = 'none';

export interface AvatarColorPickerProps extends Omit<
    ColorSwatchPickerProps,
    'trigger' | 'children' | 'value' | 'defaultValue' | 'onChange'
> {
    // What the avatar preview shows; `name` is the form field's.
    avatarName: string;
    avatarSrc?: string;
    value?: number | null;
    onChange?: (value: number | null) => void;
}

const AvatarColorPicker = ({ avatarName, avatarSrc, value = null, onChange, ...props }: AvatarColorPickerProps) => {
    const { t } = useTranslation();

    return (
        <ColorSwatchPicker
            aria-label={t('avatarColor.pick')}
            {...props}
            value={value === null ? NO_COLOR : String(value)}
            onChange={(radioValue) => onChange?.(radioValue === NO_COLOR ? null : Number(radioValue))}
            trigger={<Avatar name={avatarName} src={avatarSrc} hue={value} size="md" />}
        >
            <ColorSwatchPicker.Swatch value={NO_COLOR} aria-label={t('avatarColor.none')} />
            {(Object.entries(AVATAR_COLORS) as [AvatarColorName, number][]).map(([name, hue]) => (
                <ColorSwatchPicker.Swatch
                    key={name}
                    value={String(hue)}
                    aria-label={t(`avatarColor.colors.${name}`)}
                    hue={hue}
                />
            ))}
        </ColorSwatchPicker>
    );
};

export default AvatarColorPicker;
